
/* prettier-ignore */

import { Injectable, computed, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, Subscription, catchError, interval, tap, throwError } from 'rxjs';
import { ChatStylesRequest } from '../interface/chatStyles.interface';
import { Record } from '../interface/history.interface';
import { DataChatService } from './dataChat.service';
import { CssStylesService } from './cssStyles.service';
import { AzurePrompt } from '../interface/azurePrompt.interface';
import { Question } from '../interface/question.interface';
import { Message } from '../interface/message.interface';
import { ScrollService } from './scrollContainer.service';
import { TranslateService } from '@ngx-translate/core';
import { RestService } from 'src/app/services/rest.service';
import { DebugService } from 'src/app/services/debug.service';
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: 'root',
})

export class RequestService {
  private debugService = inject(DebugService);

  private rest = inject(RestService);
  private baseUrl: string = `${environment.apiAssistantUrlBase}/api`; //environments.baseUrl;

  public headers = new HttpHeaders({
    'Content-Type': 'application/json',
    Authorization: 'Bearer 3db0e94b-d4ec-4e86-8350-894a1432f663',
  });

  public cssStyles = inject(CssStylesService);
  public http = inject(HttpClient);
  public dataChat = inject(DataChatService);
  public scrollService = inject(ScrollService);
  public translateService = inject(TranslateService)
  public durationChatSubscription?: Subscription

  private messagesSubject = new Subject<void>();

  getHistory() {
    let data = JSON.stringify({
      token: this.dataChat.token(),
      chat_id: this.dataChat.id(),
    });
    return this.http.post<Record>(`${this.baseUrl}/get-back-chatbot`, data, {
      headers: this.headers,
    });
  }

  getHistoryChatbase() {
    let data = JSON.stringify({
      token: this.dataChat.token(),
      chat_id: this.dataChat.id(),
    });
    return this.http.post<Record>(
      `${this.baseUrl}/get-back-chatbot-chatbase`,
      data,
      { headers: this.headers }
    );
  }

  getHistoryAzure() {
    let data = JSON.stringify({
      token: this.dataChat.token(),
      chat_id: this.dataChat.id(),
    });
    return this.http.post<Record>(`${this.baseUrl}/get-back-chatbot`, data, {
      headers: this.headers,
    });
  }

  saveChat(email: string, name: string | null): Observable<any> {
    let data = JSON.stringify({
      token: this.dataChat.token(),
      chat_id: this.dataChat.id(),
      email,
      name,
      lang: this.dataChat.lang(),
      new: this.dataChat.isNew(),
    });
    return this.http.post(`${this.baseUrl}/send-email-conversation`, data, {
      headers: this.headers,
    }).pipe(
      catchError((error: unknown) => {
        if (error instanceof Error) {
          this.debugService.error(error, '[RequestService] Error in saveChat');
        }
        return throwError(() => new Error('Error in saveChat'));
      })
    );
  }

  sendConversation(messages: any): Observable<any> {
    let data: any = {
      token: this.dataChat.token(),
      chat_id: this.dataChat.id(),
    };

    data = {
      ...data,
      messages,
    };

    return this.http.post(`${this.baseUrl}/save-chatbase-conversation`, data, {
      headers: this.headers,
    });
  }

  getMessages(): Observable<ChatStylesRequest> {
    return this.http
      .get(
        `${this.baseUrl}/get-cicerone-data-chat/${this.dataChat.id() || '18'
        }/${this.dataChat.lang()}`
      )
      .pipe(
        catchError((error: any) => {
          return throwError(
            () =>
              new Error(
                `No se han podido sincronizar los datos: ${error.error.message}`
              )
          ) as Observable<ChatStylesRequest>;
        }),
        tap((data: any) => {
          //console.log({data})
        })
      );
  }


  getChatDataStyles(): Observable<ChatStylesRequest> {
    return this.http
      .get(`${this.baseUrl}/get-cicerone-data-chat/${this.dataChat.id()}/${this.dataChat.lang()}`)
      .pipe(
        catchError((error: any) => {
          return throwError(
            () =>
              new Error(
                `No se han podido sincronizar los datos: ${error.error.message}`
              )
          ) as Observable<ChatStylesRequest>;
        }),
        tap((data: any) => {
          if (data.status === 'Error') return;
          this.dataChat.chatbaseId.set(data.data.chatbase_id);
          this.cssStyles.getGlobalStyle(data);
          this.cssStyles.setCSSVariables();

          this.dataChat.infoMessage.set(data.data?.info_message);
          this.dataChat.isNew.set(data.data?.new);

          this.dataChat.isAzure.set(!this.dataChat.chatbaseId());
          this.dataChat.name.set(data.data.name);
          this.dataChat.azureSystemPrompt.set(data.data.base_prompt);
          this.dataChat.messagesForAzure.update((prev) => [
            ...prev,
            { role: 'system', content: this.dataChat.azureSystemPrompt() },
          ]);
          this.dataChat.requestIsFulliled.set(true);
        })
      );
  }

  sendImage(data: FormData): Observable<any> {
    return this.http.post(`${this.baseUrl}/chat/image`, data, {
      reportProgress: true,
      observe: 'events'
    });
  }

  async getResponseFromChatbase(question: Question) {
    this.dataChat.chatIsTyping.set(true);
    this.dataChat.isStreaming.set(true);

    try {
      const response = await fetch('https://www.chatbase.co/api/v1/chat', {
        signal: this.dataChat.abortSignal().signal,
        method: 'POST',
        headers: {
          Authorization: 'Bearer 3db0e94b-d4ec-4e86-8350-894a1432f663',
        },
        body: JSON.stringify(question),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw Error(errorData.message);
      }

      const data = response.body;

      if (!data) {
        // error happened
        throw Error('No response data available');
      }

      this.dataChat.chatIsTyping.set(false);
      const reader = data?.getReader();
      const decoder = new TextDecoder();
      let done = false;

      this.dataChat.messages.update((prev) => [
        ...prev,
        { content: '', role: 'assistant', show: true },
      ]);

      let text = '';

      while (!done) {
        const { value, done: doneReading } = await reader!.read();
        done = doneReading;
        const chunkValue = decoder.decode(value);
        text += chunkValue;
        this.handleStreamResponse(text);
        this.scrollService.scrollToBottom();
      }

      this.dataChat.messagesForChatBase.update((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: text,
        },
      ]);

      let pairQuestionAnswer = this.dataChat.messages().slice(-2);

      this.sendConversation(pairQuestionAnswer).subscribe();
      this.dataChat.canSaveChat.set(true);


    } catch (error: unknown) {
      this.dataChat.chatIsTyping.set(false);
      if (error instanceof Error) {
        this.debugService.error(error.name, '[RequestService] Error in getResponderAnswerStreaming');
        if (error.name === 'AbortError') return;
      }
      this.dataChat.messages.update((prev) => [
        ...prev,
        {
          content: this.translateService.instant('Fallo en la comunicación, vuelve a intentarlo'),
          role: 'assistant',
          show: true,
          danger: true,
        },
      ]);
      throw error;
    } finally {
      this.dataChat.isStreaming.set(false);
      this.scrollService.scrollToBottom();
    }
  }

  async getResponseFromAzure(data: AzurePrompt) {
    this.dataChat.chatIsTyping.set(true);
    this.dataChat.isStreaming.set(true);

    // const requestOptions = {
    //   method: 'POST',
    //   signal: this.dataChat.abortSignal().signal,
    //   headers: {
    //     'Content-Type': 'application/json',
    //     ...(this.dataChat.debug() && {'X-Debug-Mode': 'true'})
    //   },
    //   body: JSON.stringify(data),
    // };

    const message = data.messages.filter(e => e.role == 'user');
    const msg = message[message.length - 1]?.content

    const newData = {
      question: msg,
      service: parseInt(data.chatbot_id),
      user_id: data.conversation_id
    }

    const requestOptions = {
      method: 'POST',
      signal: this.dataChat.abortSignal().signal,
      headers: {
        'Content-Type': 'application/json',
        ...(this.dataChat.debug() && { 'X-Debug-Mode': 'true' })
      },
      body: JSON.stringify(newData),
    };

    try {
      let response = await fetch(
        // 'https://gpt.cms-iurban.com/ask/',
        'https://gpt.cms-iurban.com/v2/ask',
        requestOptions
      );

      if (response.status === 429) {
        this.dataChat.messages.update((prev) => [
          ...prev,
          {
            content: this.translateService.instant('Has alcanzado el limite de mensajes, por favor, inténtelo más tarde'),
            role: 'assistant',
            show: true,
          },
        ])
        this.scrollService.scrollToBottom();
        this.dataChat.isStreaming.set(false);
        this.dataChat.chatIsTyping.set(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw Error(errorData.message);
      }

      const data = response.body;

      await this.readChunk(data);
    } catch (error) {
      this.dataChat.chatIsTyping.set(false);
      if (error instanceof Error) {
        this.debugService.error(error.name, '[RequestService] Error in getResponderAnswerStreaming');
        if (error.name === 'AbortError') return;
      }
      this.dataChat.messages.update((prev) => [
        ...prev,
        {
          content: this.translateService.instant('Fallo en la comunicación, vuelve a intentarlo'),
          role: 'assistant',
          show: true,
          danger: true,
        },
      ]);
      throw error;
    } finally {
      this.dataChat.isStreaming.set(false);
      setTimeout(() => {
        this.scrollService.scrollToBottom();
      }, 10);
    }
  }

  handleStreamResponse(message: string) {
    if (this.isBot()) this.dataChat.messages().pop();
    this.dataChat.messages.update((prev) => [
      ...prev,
      {
        content: message,
        role: 'assistant',
        show: true,
      }])
  }

  isBot() {
    return this.dataChat.messages().at(-1)?.role !== 'user'
  }

  async readChunk(data: ReadableStream | null) {
    if (!data) {
      throw Error('No response data available');
    }

    const reader = data?.getReader();
    const decoder = new TextDecoder();
    let done = false;

    let text: string = '';

    let initMessage: Message = { role: 'assistant', content: '', show: true };

    let bubbleIsCreated = false;

    while (!done) {
      const { value, done: doneReading } = await reader!.read();

      done = doneReading;

      const chunkValue = decoder.decode(value);

      if (!bubbleIsCreated) {
        this.dataChat.chatIsTyping.set(false);
        this.dataChat.messages.update((prev) => [...prev, initMessage]);
        bubbleIsCreated = true;
      }

      // text += chunkValue;
      // this.handleStreamResponse(text + '<span class="typing"></span>');
      // this.scrollService.scrollToBottom();

      for (const char of chunkValue) {
        text += char;
        this.handleStreamResponse(text + '<span class="typing"></span>');
        await this.delay(10); // Retraso de 10ms entre cada carácter para la animación
      }
      this.scrollService.scrollToBottom();
    }

    this.dataChat.canSaveChat.set(true);
    this.handleStreamResponse(text);
    this.scrollService.scrollToBottom();
    this.dataChat.isStreaming.set(false);

    this.dataChat.messagesForAzure.update((prev) => [
      ...prev,
      { role: 'assistant', content: text },
    ]);
  }

  private async delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
